/* eslint-disable no-cond-assign */

// Namespaces
import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { DataPoints } from '../DataPoints';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';

// Components
import { DateCell, NotApplicableCell, PercentageCell, TextCell } from '@/views/components/cells';

// Types
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import LineSeriesDataPoint from '@/types/line-series-data-point';
import RollingIntervals from '@/types/rolling-intervals';
import ComparisonItem from '@/types/comparison-item';
import Dictionary from '@/types/dictionary';
import LocalizedString from '@/types/localized-string';

// Utils
import { ROLLING_INTERVALS } from '@/utils/portfolios';
import { getRollingIntervalFromDates } from '@/utils/charts';
import portfolioUtils from '@/utils/portfolio';
import { deepClone } from '@/utils/objects';
import { TIME_PERIODS } from '@/utils/constants';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import { monthsToPeriod } from '@/utils/time';

export default class DrawdownDetailsDAO extends ComparisonItemBasedProposalDAO {
  changeDateFormat?: string;

  labelOverride?: Dictionary<LocalizedString>;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new DrawdownDetailsDAO(proposal, proposalElement);
  }

  toolbarOption: ProposalToolbarOption | undefined;

  interval: RollingIntervals;

  startDate: Date | null;

  endDate: Date | null;

  items: ComparisonItem[];

  constructor(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement) {
    super(proposal, proposalElement);
    this.toolbarOption = {};
    this.interval = ROLLING_INTERVALS.WEEKLY;
    this.startDate = null;
    this.endDate = null;
    this.items = proposal.items;
  }

  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    const daoConfig: ProposalNS.DrawdownDetailsConfig = this.proposalElement.DAO as ProposalNS.DrawdownDetailsConfig;

    if (daoConfig.changeDateFormat !== undefined) {
      this.changeDateFormat = daoConfig.changeDateFormat;
    }

    if (daoConfig.labelOverride !== undefined) {
      this.labelOverride = daoConfig.labelOverride;
    }

    let itemDAO: ComparisonItemDAO | null = null;
    this.toolbarOption = this.getToolbarOption();
    if (this.toolbarOption != null) {
      this.startDate = this.toolbarOption.startDate != null ? new Date(this.toolbarOption.startDate) : null;
      this.endDate = this.toolbarOption.endDate != null ? new Date(this.toolbarOption.endDate) : new Date();
      this.interval = getRollingIntervalFromDates(this.startDate, this.endDate, this.items, this.toolbarOption.useMonthly);
    }

    let savedDisclaimerKeys: string[] = [];

    while ((itemDAO = this.nextItem()) !== null) {
      if (this.endDate != null) {
        const clone = deepClone(itemDAO.item);
        if (this.proposal.showBaseCurrencies === true && clone.performanceBase) {
          const holdings = deepClone(clone.holdings).map(h => ({ ...h, rolling: h.baseRolling }));
          const fees = (clone.discretionaryMer || 0) + (clone.tax || 0);
          // rebalance should drive off of what is in the comparison growth settings. Since we are rethinking all this,
          // let's go with the default for now.
          clone.performanceBase.rolling = portfolioUtils.calculateGrowth(holdings, fees, undefined, this.proposal.shortHistory);
        }

        const monthsRange = this.toolbarOption?.monthsRange;
        const period = monthsRange && monthsToPeriod(monthsRange);

        const isSinceInception = period === TIME_PERIODS.INC;
        const isMaxCommonHistory = period === TIME_PERIODS.CI;

        let overrideStartDate;
        const commonInceptionStart = this.proposal.commonInception?.startTimestamp;
        if (isMaxCommonHistory && commonInceptionStart) {
          overrideStartDate = new Date(commonInceptionStart);
        }

        const { response, disclaimerKeys } = DataPoints.getDrawdownDetailsData(
          clone,
          this.interval,
          overrideStartDate ?? this.startDate,
          this.endDate,
          this.proposal.showBaseCurrencies === true,
        );

        // add it once.
        if (disclaimerKeys.length > 0 && savedDisclaimerKeys.length === 0) {
          const { shortHistory } = this.proposal;
          const showSinceInceptionDisclaimer = isSinceInception && shortHistory;
          const showMaxCommonDisclaimer = isMaxCommonHistory && !shortHistory;

          savedDisclaimerKeys = [
            ...disclaimerKeys,
            ...(showSinceInceptionDisclaimer ? [PROPOSAL_DISCLAIMERS.SINCE_INCEPTION_DATE] : []),
            ...(showMaxCommonDisclaimer ? [PROPOSAL_DISCLAIMERS.COMMON_INCEPTION_DATE] : []),
          ];
        }

        const getLabelOverride = (key: string) => {
          const { locale } = this.proposal.options;

          return (this.labelOverride && this.labelOverride[key] && this.labelOverride[key][locale]) || this.formatMessage(key);
        };

        this.addItemField(
          itemDAO,
          getLabelOverride('Proposal.Modules.DrawdownDetails.MaxDrawdown'),
          response.maxDrawdown != null ? response.maxDrawdown : undefined,
          response.maxDrawdown != null ? PercentageCell : NotApplicableCell,
        );

        this.addItemField(
          itemDAO,
          getLabelOverride('Proposal.Modules.DrawdownDetails.NumOfPeriods'),
          response.drawdownPeriodCount !== undefined ? response.drawdownPeriodCount : undefined,
          response.drawdownPeriodCount !== undefined ? TextCell : NotApplicableCell,
        );

        this.addItemField(
          itemDAO,
          this.formatMessage('Proposal.Modules.DrawdownDetails.PeakDate'),
          response.peakTimestamp !== undefined ? response.peakTimestamp : undefined,
          response.peakTimestamp !== undefined ? DateCell : NotApplicableCell,
          this.changeDateFormat ? this.changeDateFormat : undefined,
        );

        this.addItemField(
          itemDAO,
          this.formatMessage('Proposal.Modules.DrawdownDetails.ValleyDate'),
          response.valleyTimestamp !== undefined ? response.valleyTimestamp : undefined,
          response.valleyTimestamp !== undefined ? DateCell : NotApplicableCell,
          this.changeDateFormat ? this.changeDateFormat : undefined,
        );

        this.addItemField(
          itemDAO,
          this.formatMessage('Proposal.Modules.DrawdownDetails.RecoveryDate'),
          response.recoveryTimestamp !== undefined ? response.recoveryTimestamp : undefined,
          response.recoveryTimestamp !== undefined ? DateCell : NotApplicableCell,
          this.changeDateFormat ? this.changeDateFormat : undefined,
        );

        this.addItemField(
          itemDAO,
          this.formatMessage('Proposal.Modules.DrawdownDetails.PeriodsToRecovery'),
          response.recoveryPeriodCount !== undefined ? response.recoveryPeriodCount : undefined,
          response.recoveryPeriodCount !== undefined ? TextCell : NotApplicableCell,
        );
      }
    }

    const series: LineSeriesDataPoint[][] = [];
    const itemDAOs: ComparisonItemDAO[] = this.getItemDAOs();
    for (let i = 0; i < itemDAOs.length; i++) {
      const data: LineSeriesDataPoint[] = [[0, 0]]; // we do not actually need real data here for the disclaimer to show.
      series.push(data);
    }

    for (const disclaimerKey of savedDisclaimerKeys) {
      this.addDisclaimerConfigs([
        {
          key: disclaimerKey,
          config: { series, interval: this.interval },
          items: itemDAOs.map(item => item.item),
        },
      ]);
    }
  }

  // private methods

  private addItemField(itemDAO: ComparisonItemDAO, name: string, value: number | undefined, cellType: any, format?: string): void {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value !== undefined ? value : undefined, ProposalStyleClassNames.ProposalTableContent, cellType);
    field.cellFormat = format;
    this.addFieldDAO(itemDAO, field);
    this.addFieldName(name, ProposalStyleClassNames.ProposalTableContent);
  }
}
